import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Select,
  SelectProps,
} from '@amzn/awsui-components-react-v3';
import React, { useEffect, useState } from 'react';
import { editDatabase } from 'src/api/catalog';
import { Toggle } from '@amzn/awsui-components-react-v3/polaris';
import { DatabaseInfo } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { AutoOnboardFilterInput } from 'src/components/common/AutoOnboardFilterInput';
import { defaultVisibility, visibilitySelection } from 'src/commons/constants';
import { findOption } from 'src/components/catalog/common';

export interface DatabaseEditModalProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  database: DatabaseInfo;
  visible: boolean;
  dismiss: any;
  notifyEditSuccess: any;
  notifyEditFailure: any;
}

export const DatabaseEditModal = (props: DatabaseEditModalProps) => {
  const [autoTableOnboard, setAutoTableOnboard] = useState<boolean>(undefined);
  const [eventSubscriptions, setEventSubscriptions] = useState<boolean>(undefined);
  const [databaseDescription, setDatabaseDescription] = useState<string>(undefined);
  const [databaseName, setDatabaseName] = useState<string>(undefined);
  const [includePatterns, setIncludePatterns] = useState<string[]>([]);
  const [excludePatterns, setExcludePatterns] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<SelectProps.Option>(defaultVisibility);

  const handleEdit = async () => {
    try {
      let response = await editDatabase({
        DataSourceId: props.database.DataSourceId,
        DatabaseName: props.database.DatabaseName,
        CatalogId: props.database.CatalogId,
        ClusterIdentifier: props.database.ClusterIdentifier,
        RedshiftWorkgroupName: props.database.RedshiftWorkgroupName,
        BucketName: props.database.BucketName,
        Region: props.database.Region,
        Description: databaseDescription,
        Visibility: visibility.value,
        AutoTableOnboard: autoTableOnboard,
        AutoOnboardConfig: autoTableOnboard
          ? {
              IncludePatterns: includePatterns.length > 0 ? includePatterns : undefined,
              ExcludePatterns: excludePatterns.length > 0 ? excludePatterns : undefined,
            }
          : undefined,
        AllowSNSEventSubscription: eventSubscriptions,
      });
      props.notifyEditSuccess(response.Message);
    } catch (err) {
      props.notifyEditFailure('Exception edit database: ' + err.message);
    }
    props.dismiss();
  };

  const handleRefresh = async () => {
    setDatabaseDescription(props.database?.Description);
    setDatabaseName(props.database?.DatabaseName);
    setAutoTableOnboard(props.database?.AutoTableOnboard);
    setEventSubscriptions(props.database?.AllowSNSEventSubscription);
    setIncludePatterns(props.database?.AutoOnboardConfig?.IncludePatterns || []);
    setExcludePatterns(props.database?.AutoOnboardConfig?.ExcludePatterns || []);
    if (props.database.Visibility) {
      setVisibility(findOption(visibilitySelection, props.database.Visibility));
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={props.dismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleEdit}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
      header='Edit database'
    >
      <ColumnLayout>
        <FormField label='Database name'>
          <Input
            placeholder={'Database name'}
            value={databaseName}
            onChange={(e) => {
              setDatabaseName(e.detail.value);
            }}
            disabled
          />
        </FormField>
        <FormField label='Database description'>
          <Input
            placeholder={'Database description'}
            value={databaseDescription}
            onChange={(e) => {
              setDatabaseDescription(e.detail.value);
            }}
          />
        </FormField>
        <FormField label='Data visibility' description='Data visibility defines how your database is discoverable'>
          <Select
            placeholder='Public'
            selectedOption={visibility}
            options={visibilitySelection}
            ariaRequired={true}
            onChange={({ detail }) => {
              setVisibility(detail.selectedOption);
            }}
            disabled={false}
          />
        </FormField>
        <FormField
          label='Auto table onboard'
          description='Select this to automatically register new datasets from this database into Omni.'
        >
          <Toggle onChange={(e) => setAutoTableOnboard(e.detail.checked)} checked={autoTableOnboard} />
        </FormField>
        {autoTableOnboard && (
          <AutoOnboardFilterInput
            includePatterns={includePatterns}
            excludePatterns={excludePatterns}
            onIncludePatternsChange={setIncludePatterns}
            onExcludePatternsChange={setExcludePatterns}
          />
        )}
        <FormField label='Allow SNS event subscriptions' description={'Allows subscriptions for event notifications.'}>
          <Toggle onChange={(e) => setEventSubscriptions(e.detail.checked)} checked={eventSubscriptions} />
        </FormField>
      </ColumnLayout>
    </Modal>
  );
};
